import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import designTokens from '@/design-tokens';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useOnboardingProgress } from '@/contexts/OnboardingProgressContext';
import type { OnboardingStageState, OnboardingStepState } from '@/utils/onboardingStages';
import { ProgressCheck } from '@/components/ui/Icons/ProgressCheck';
import { ArrowRight } from '@/components/ui/Icons/ArrowRight';
import { ChevronLeft } from '@/components/ui/Icons/ChevronLeft';
import { Modal } from '@/components/ui/Modals/Modal';
import Close from '@/components/ui/Icons/Close';
import ToastFlip from '@/components/ui/Illustrations/ToastFlip';
import CelebrationToast from '@/components/ui/Illustrations/CelebrationToast';
import { PrimaryButton } from '@/components/ui/Buttons/Primary';
import { SecondaryButton } from '@/components/ui/Buttons/Secondary';
import { TextButton } from '@/components/ui/Buttons/Text';
import { TextLink } from '@/components/ui/Buttons/TextLink';
import { Chip } from '@/components/ui/Chip/Chip';
import { ProgressBar } from '@/components/ui/ProgressBar';
import ClipboardCheckmark from '@/components/ui/Icons/ClipboardCheckmark';

// ---------------------------------------------------------------------------
// The Getting Started guide (TENANT_SETUP_SEQUENCE_GAPS W1–W10, session 331).
//
// Three stages named for what the owner gets out of them. The step list is
// `constants/onboardingSteps.ts` and the arithmetic `utils/onboardingStages.ts`
// — the same ones the dashboard banner and the step-complete toast read, so
// the three can never disagree about what is next.
//
// No step gates another: completion is data-detected (detectOnboardingProgress),
// so a paper-first tenant who hand-adds records ticks steps as they go, and one
// spreadsheet upload ticks most of Stage 1 at once.
// ---------------------------------------------------------------------------

type ModalView = 'main' | 'cost' | 'sell' | 'run';

const font = designTokens['font/family/body'] || 'Montserrat, sans-serif';

// ---------------------------------------------------------------------------
// Keyframes (injected once)
// ---------------------------------------------------------------------------

const STYLE_ID = 'onboarding-modal-keyframes';
function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes onboardSlideUp {
      from { opacity: 0; transform: translateY(60px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes onboardSlideLeft {
      from { opacity: 0; transform: translateX(40px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes onboardSlideRight {
      from { opacity: 0; transform: translateX(-40px); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Tile Components
// ---------------------------------------------------------------------------

interface TileProps {
  label: string;
  subtext: string;
  completed: boolean;
  isLast: boolean;
  onClick: () => void;
}

const Tile: React.FC<TileProps> = ({ label, subtext, completed, isLast, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 0',
        borderBottom: isLast ? 'none' : `1px solid ${designTokens['border/color/default'] || '#dee2e6'}`,
        cursor: 'pointer',
      }}
    >
      {/* Left: ProgressCheck */}
      <div style={{ flexShrink: 0 }}>
        <ProgressCheck completed={completed} size={20} />
      </div>

      {/* Center: label + subtext */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <div
          style={{
            fontFamily: designTokens['font/family/body'] || 'Montserrat, sans-serif',
            fontWeight: designTokens['font/weight/medium'] || '500',
            fontSize: designTokens['font/size/sm'] || '0.875rem',
            lineHeight: designTokens['font/line-height/sm'] || '1.25rem',
            color: hovered
              ? (designTokens['text/brand'] || '#2bb673')
              : (designTokens['text/primary'] || '#000000'),
            transition: 'color 0.15s ease',
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontFamily: designTokens['font/family/body'] || 'Montserrat, sans-serif',
            fontWeight: designTokens['font/weight/regular'] || '400',
            fontSize: designTokens['font/size/xs'] || '0.75rem',
            lineHeight: designTokens['font/line-height/xxs'] || '1rem',
            color: designTokens['text/secondary'] || '#6c757d',
          }}
        >
          {subtext}
        </div>
      </div>

      {/* Right: arrow */}
      <div
        style={{
          flexShrink: 0,
          width: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: designTokens['button/radius'] || '0.5rem',
          backgroundColor: hovered
            ? (designTokens['button/secondary/fill/hover'] || '#f0fef7')
            : 'transparent',
          transition: 'background-color 0.2s ease-in-out',
        }}
      >
        <ArrowRight width={20} height={20} color={designTokens['icon/color/brand'] || '#2bb673'} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step row — a Tile with the step's pitch, and its skip / undo / plan action
// ---------------------------------------------------------------------------

interface StepRowProps {
  step: OnboardingStepState;
  isLast: boolean;
  onOpen: () => void;
  onSkip: () => void;
  onUndo: () => void;
}

const StepRow: React.FC<StepRowProps> = ({ step, isLast, onOpen, onSkip, onUndo }) => {
  const [hovered, setHovered] = useState(false);
  const { def, status, locked } = step;
  const skipped = status === 'skipped';
  const muted = designTokens['text/placeholder'] || '#adb5bd';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-step={def.key}
      data-status={status}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 0',
        borderBottom: isLast ? 'none' : `1px solid ${designTokens['border/color/default'] || '#dee2e6'}`,
        cursor: 'pointer',
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <ProgressCheck completed={status === 'done'} size={20} />
      </div>

      {/* Label + pitch. A skipped row keeps its pitch (W5): the skip says what is true today,
          the pitch says what changes if they try it. */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span
            style={{
              fontFamily: font,
              fontWeight: designTokens['font/weight/medium'] || '500',
              fontSize: designTokens['font/size/sm'] || '0.875rem',
              lineHeight: designTokens['font/line-height/sm'] || '1.25rem',
              color: skipped
                ? muted
                : hovered
                  ? (designTokens['text/brand'] || '#2bb673')
                  : (designTokens['text/primary'] || '#000000'),
              transition: 'color 0.15s ease',
            }}
          >
            {def.label}
          </span>
          {locked && <Chip status="info" size="small">Growth</Chip>}
        </div>
        <div
          style={{
            fontFamily: font,
            fontWeight: designTokens['font/weight/regular'] || '400',
            fontSize: designTokens['font/size/xs'] || '0.75rem',
            lineHeight: designTokens['font/line-height/xxs'] || '1rem',
            color: skipped ? muted : (designTokens['text/secondary'] || '#6c757d'),
          }}
        >
          {def.pitch}
        </div>

        {/* The skip sits UNDER the pitch: the pitch asks a question ("Charge sales tax? …") and the
            skip is its answer, and the pitch keeps the full row width (Percy, session 331).
            Its own container stops the row's click. Both states stay MOUNTED in one grid cell, the
            inactive one hidden, so swapping them cannot change the row's height and move the modal
            (CLAUDE.md §Modal — reserve the space structurally). */}
        {def.skipLabel && status !== 'done' && (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ display: 'grid', alignItems: 'center', justifyItems: 'start', alignSelf: 'flex-start', marginTop: '2px' }}
          >
            <div
              aria-hidden={!skipped}
              style={{
                gridArea: '1 / 1',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                visibility: skipped ? 'visible' : 'hidden',
              }}
            >
              <span style={{ fontFamily: font, fontSize: designTokens['font/size/xs'] || '0.75rem', color: muted }}>
                Skipped
              </span>
              <span aria-hidden="true" style={{ color: muted }}>·</span>
              <TextLink size="small" onClick={onUndo} disabled={!skipped}>
                Undo
              </TextLink>
            </div>
            <div
              aria-hidden={skipped}
              style={{ gridArea: '1 / 1', display: 'flex', alignItems: 'center', visibility: skipped ? 'hidden' : 'visible' }}
            >
              <TextLink size="small" onClick={onSkip} disabled={skipped}>
                {def.skipLabel}
              </TextLink>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          flexShrink: 0,
          width: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: designTokens['button/radius'] || '0.5rem',
          backgroundColor: hovered
            ? (designTokens['button/secondary/fill/hover'] || '#f0fef7')
            : 'transparent',
          transition: 'background-color 0.2s ease-in-out',
        }}
      >
        <ArrowRight width={20} height={20} color={designTokens['icon/color/brand'] || '#2bb673'} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Modal Component
// ---------------------------------------------------------------------------

interface OnboardingGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OnboardingGuideModal: React.FC<OnboardingGuideModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const visibility = useFeatureVisibility();
  const { progress, guide, skipStep, undoSkip, openStep } = useOnboardingProgress();
  const [view, setView] = useState<ModalView>('main');
  const slideDirection = useRef<'left' | 'right'>('left');
  const [slideKey, setSlideKey] = useState(0);
  // Import-vs-scratch fork shown to tenants with zero catalog data. Dismissal
  // sticks for the session (not reset on reopen); once ANY catalog record
  // exists the hero stops appearing on its own.
  const [heroDismissed, setHeroDismissed] = useState(false);
  // A finished guide shows the celebration; "Review your steps" opens the stages again so a
  // skipped step can still be undone (Percy's call 1, session 331).
  const [reviewing, setReviewing] = useState(false);

  useEffect(ensureKeyframes, []);

  // Reset to main view when modal opens
  useEffect(() => {
    if (isOpen) {
      setView('main');
      setReviewing(false);
    }
  }, [isOpen]);

  const navigateToView = useCallback((target: ModalView) => {
    slideDirection.current = target === 'main' ? 'right' : 'left';
    setSlideKey((k) => k + 1);
    setView(target);
  }, []);

  const { stages, finished: completedSteps, total: totalSteps, allComplete } = guide;
  const showCelebration = allComplete && !reviewing;

  const hasNoCatalog =
    !!progress &&
    !progress.ingredients &&
    !progress.materials &&
    !progress.recipes &&
    !progress.packages &&
    !progress.products;
  const showHero = hasNoCatalog && !heroDismissed && !allComplete;

  const handleImport = useCallback(() => {
    onClose();
    // The Recipes page opens the spreadsheet import modal on arrival.
    navigate('/recipes', { state: { openImportModal: true } });
  }, [onClose, navigate]);

  const currentStage: OnboardingStageState | undefined =
    view !== 'main' ? stages.find((stage) => stage.def.key === view) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="auto"
      padding={designTokens['modal/padding'] || '24px'}
      closeOnOverlayClick
      style={{ width: '640px', animation: 'onboardSlideUp 0.35s ease-out' }}
    >
      {/* ---- Header area ---- */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Top bar: Back + Close */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          {view !== 'main' ? (
            <button
              onClick={() => navigateToView('main')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: font,
                fontWeight: designTokens['font/weight/medium'] || '500',
                fontSize: designTokens['font/size/sm'] || '0.875rem',
                color: designTokens['text/brand'] || '#2bb673',
                padding: '4px 0',
              }}
            >
              <ChevronLeft width={16} height={16} color={designTokens['text/brand'] || '#2bb673'} />
              Back
            </button>
          ) : (
            <div />
          )}

          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Close"
          >
            <Close />
          </button>
        </div>

        {/* Illustration + Title + Progress */}
        {!showCelebration && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '16px',
              marginBottom: '24px',
            }}
          >
            <div style={{ flexShrink: 0 }}>
              <ToastFlip width={88} height={88} />
            </div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Icon on the left of BOTH lines — the stage name never sits beneath it (Percy, session 331). */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* The stage views drop the icon (Percy, session 331); the guide's main view keeps it. */}
                {!currentStage && (
                  <div style={{ flexShrink: 0, display: 'flex' }}>
                    <ClipboardCheckmark />
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                  <span
                    style={{
                      fontFamily: designTokens['font/family/body'] || 'Montserrat, sans-serif',
                      fontWeight: designTokens['font/weight/bold'] || '700',
                      fontSize: designTokens['font/size/md'] || '1rem',
                      color: designTokens['text/primary'] || '#000000',
                    }}
                  >
                    Getting Started Guide
                  </span>
                  {currentStage && (
                    <span
                      style={{
                        fontFamily: font,
                        // Medium: one step lighter than the title above it (Percy, session 331).
                        fontWeight: designTokens['font/weight/medium'] || '500',
                        fontSize: designTokens['font/size/sm'] || '0.875rem',
                        lineHeight: designTokens['font/line-height/sm'] || '1.25rem',
                        color: designTokens['text/secondary'] || '#6c757d',
                      }}
                    >
                      {currentStage.def.title}
                    </span>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: designTokens['field/gap/horizontal'] || '1rem',
                }}
              >
                <ProgressBar percent={totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0} style={{ flex: 1 }} />
                <span
                  style={{
                    fontFamily: font,
                    fontWeight: designTokens['font/weight/semibold'] || '600',
                    fontSize: designTokens['font/size/sm'] || '0.875rem',
                    lineHeight: designTokens['font/line-height/sm'] || '1.25rem',
                    color: designTokens['text/primary'] || '#000000',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {completedSteps} of {totalSteps}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Celebration state */}
        {showCelebration && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: designTokens['field/gap/vertical'] || '1rem',
              padding: '24px 0',
            }}
          >
            <CelebrationToast width={240} height={160} />
            <h2
              style={{
                fontFamily: designTokens['font/family/body'] || 'Montserrat, sans-serif',
                fontWeight: designTokens['font/weight/bold'] || '700',
                fontSize: designTokens['font/size/md'] || '1rem',
                color: designTokens['text/primary'] || '#000000',
                margin: 0,
              }}
            >
              You&apos;re all set!
            </h2>
            <p
              style={{
                fontFamily: font,
                fontWeight: designTokens['font/weight/regular'] || '400',
                fontSize: designTokens['font/size/sm'] || '0.875rem',
                color: designTokens['text/primary'] || '#000000',
                margin: 0,
                maxWidth: '480px',
              }}
            >
              Your setup is complete. As you add sales orders, your dashboard will fill with real cost and margin data.
            </p>
            <PrimaryButton size="large" onClick={onClose}>
              View Dashboard
            </PrimaryButton>
            <TextButton onClick={() => setReviewing(true)}>Review Your Steps</TextButton>
          </div>
        )}
      </div>

      {/* ---- Body: stages, or one stage's steps ---- */}
      {!showCelebration && (
        <div style={{ overflow: 'hidden' }}>
          <div
            key={slideKey}
            style={{
              animation: `${slideDirection.current === 'left' ? 'onboardSlideLeft' : 'onboardSlideRight'} 0.25s ease-out`,
            }}
          >
            {view === 'main' ? (
              <div>
                {showHero && (
                  <div
                    style={{
                      backgroundColor: '#f0fef7',
                      border: `1px solid ${designTokens['surface/brand/primary'] || '#2bb673'}`,
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: font,
                        fontWeight: designTokens['font/weight/semibold'] || '600',
                        fontSize: designTokens['font/size/sm'] || '0.875rem',
                        color: designTokens['text/primary'] || '#343a40',
                      }}
                    >
                      Do you track your ingredients, recipes, or costs in spreadsheets?
                    </span>
                    <span
                      style={{
                        fontFamily: font,
                        fontSize: designTokens['font/size/sm'] || '0.875rem',
                        color: designTokens['text/primary'] || '#343a40',
                      }}
                    >
                      Upload any spreadsheet, in whatever layout you already use.
                      Your ingredients, recipes, packaging, and products land in one
                      go, plus the suppliers named on them, and you confirm everything
                      before it imports.
                    </span>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <PrimaryButton onClick={handleImport}>
                        Import Spreadsheet
                      </PrimaryButton>
                      <SecondaryButton onClick={() => setHeroDismissed(true)}>
                        I&apos;ll Start From Scratch
                      </SecondaryButton>
                    </div>
                  </div>
                )}
                {stages.map((stage, idx) => (
                  <Tile
                    key={stage.def.key}
                    label={stage.def.title}
                    subtext={`${stage.finished} of ${stage.total} ${stage.total === 1 ? 'step' : 'steps'} done`}
                    completed={stage.complete}
                    isLast={idx === stages.length - 1}
                    onClick={() => navigateToView(stage.def.key)}
                  />
                ))}
              </div>
            ) : currentStage ? (
              <div>
                {/* W2: the import is a shortcut, not a counted step. The stage's name now sits in the
                    header, so this row carries the import's own pitch (Percy, session 331). */}
                {currentStage.def.key === 'cost' && visibility.showRecipes && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '16px',
                      marginBottom: '12px',
                      padding: '16px',
                      // The success toast's fill and radius, with a 1px brand stroke (Percy, session 331).
                      backgroundColor: designTokens['surface/feedback/success'] || '#d5f9ee',
                      borderRadius: designTokens['radius/lg'] || '1rem',
                      border: `${designTokens['border/weight/light'] || '0.0625rem'} solid ${designTokens['surface/brand/primary'] || '#2bb673'}`,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: font,
                        fontSize: designTokens['font/size/xs'] || '0.75rem',
                        lineHeight: designTokens['font/line-height/xxs'] || '1rem',
                        color: designTokens['text/primary'] || '#000000',
                      }}
                    >
                      Track your costs in a spreadsheet? Upload it in whatever layout you use. Ingredients,
                      recipes, packaging and products land in one go.
                    </span>
                    <div style={{ flexShrink: 0 }}>
                      <SecondaryButton size="small" onClick={handleImport}>
                        Import Your Spreadsheet
                      </SecondaryButton>
                    </div>
                  </div>
                )}

                {currentStage.steps.map((step, idx) => (
                  <StepRow
                    key={step.def.key}
                    step={step}
                    isLast={idx === currentStage.steps.length - 1}
                    onOpen={() => openStep(step)}
                    onSkip={() => skipStep(step.def.key)}
                    onUndo={() => undoSkip(step.def.key)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </Modal>
  );
};
